import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Crown, CheckCircle, WarningCircle, ArrowRight, SignIn } from "@phosphor-icons/react";
import { acceptProjectTransfer } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// User lands here from the email link. Auto-accepts on mount when authenticated.
// If not authenticated, it nudges them to log in (preserving the token in the URL
// so they come right back).
export default function AcceptTransfer() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState({ status: "idle", error: "", project: null });
  // The accept POST is a one-time, side-effectful operation (consumes the token
  // server-side). Guarding with a ref ensures React 18 StrictMode's double-invoke
  // of effects doesn't fire the request twice — the second call would otherwise
  // hit a spent token and render an "already accepted" error over a successful flow.
  const calledRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      setState({ status: "error", error: "Missing token. Open the link from your email again." });
      return;
    }
    if (!user) {
      setState({ status: "needs_login", error: "" });
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;
    (async () => {
      setState({ status: "loading", error: "" });
      try {
        const res = await acceptProjectTransfer(token);
        setState({
          status: "success",
          error: "",
          project: { id: res.project_id, name: res.project_name },
        });
      } catch (err) {
        setState({
          status: "error",
          error: err?.response?.data?.detail || "Couldn't accept the transfer.",
          project: null,
        });
      }
    })();
  }, [token, user, authLoading]);

  const goLogin = () => {
    const next = encodeURIComponent(`/transfer/accept?token=${token}`);
    navigate(`/login?next=${next}`);
  };

  return (
    <div className="min-h-screen landing-pattern flex items-center justify-center px-6 py-12">
      <div
        className="w-full max-w-md bg-white border border-slate-200 rounded-md p-8 shadow-[0_12px_36px_rgba(36,22,35,0.08)]"
        data-testid="accept-transfer-card"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
            <Crown size={20} weight="fill" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              ThatGantt
            </div>
            <div className="font-display text-xl font-black tracking-tight">
              Transfer Ownership
            </div>
          </div>
        </div>

        {state.status === "loading" || authLoading ? (
          <div className="text-sm text-slate-600" data-testid="accept-transfer-loading">
            Verifying your invitation…
          </div>
        ) : null}

        {state.status === "needs_login" && (
          <div className="space-y-4" data-testid="accept-transfer-login-required">
            <p className="text-sm text-slate-700 leading-relaxed">
              Sign in to accept ownership of the project. Make sure you sign in with the same
              email address that received this invite.
            </p>
            <Button
              onClick={goLogin}
              className="w-full h-11 rounded-md bg-slate-950 hover:bg-slate-800 text-white"
              data-testid="accept-transfer-login-btn"
            >
              <SignIn size={14} weight="bold" />
              <span className="ml-2">Sign in to continue</span>
            </Button>
          </div>
        )}

        {state.status === "success" && (
          <div className="space-y-4" data-testid="accept-transfer-success">
            <div className="flex items-start gap-3">
              <CheckCircle size={20} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
              <div className="text-sm text-slate-800 leading-relaxed">
                You're now the owner of{" "}
                <strong className="text-slate-950">{state.project?.name}</strong>. The previous
                owner kept edit access on the distribution list.
              </div>
            </div>
            <Link to={`/project/${state.project?.id}`}>
              <Button
                className="w-full h-11 rounded-md btn-neon"
                data-testid="accept-transfer-open-project-btn"
              >
                Open project
                <ArrowRight size={14} weight="bold" className="ml-2" />
              </Button>
            </Link>
            <Link
              to="/dashboard"
              className="block text-center text-xs text-slate-500 hover:text-slate-950 mt-2"
            >
              Or go to dashboard
            </Link>
          </div>
        )}

        {state.status === "error" && (
          <div className="space-y-4" data-testid="accept-transfer-error">
            <div className="flex items-start gap-3">
              <WarningCircle size={20} weight="fill" className="text-rose-500 shrink-0 mt-0.5" />
              <div className="text-sm text-slate-800 leading-relaxed">{state.error}</div>
            </div>
            <Link to="/dashboard">
              <Button
                variant="outline"
                className="w-full h-11 rounded-md border-slate-300"
                data-testid="accept-transfer-back-btn"
              >
                Back to dashboard
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
